import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  SwapHoriz as SwapHorizIcon,
  Event as EventIcon,
  CalendarMonth as CalendarIcon,
  DirectionsRun as RaceIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  HelpOutline as AbsentIcon,
  Link as LinkIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
  SmartToy as AgentIcon,
  Lock as LockIcon
} from '@mui/icons-material'
import { formatDateInTimezone } from '@/utils/timezone'
import { Link } from 'react-router-dom'
import { Proposal } from '@/types'
import { FieldDiff, RaceDiff } from '@/hooks/useProposalEditor'
import { getCategoriesForEntityType } from '@/constants/fieldCategories'
import SourceTabs, { isSourceProposalReadOnly } from './SourceTabs'
import CopyFieldButton from './CopyFieldButton'
import CopyRaceButton, { CopyAllRacesButton } from './CopyRaceButton'

// ═══════════════════════════════════════════════════════════════════════════════
// Extraction des sources depuis les justifications d'une proposition
// ═══════════════════════════════════════════════════════════════════════════════

interface SourceInfo {
  url: string
  snippet?: string
  date?: string
  confidence?: number
}

/**
 * Extrait les informations de source d'une proposition
 * Cherche dans justifications et sourceMetadata
 */
function extractSourceInfo(proposal: Proposal): SourceInfo | null {
  // Cast pour accéder aux champs optionnels non typés
  const p = proposal as any

  // Priorité 1: sourceMetadata.url
  if (p.sourceMetadata?.url) {
    return {
      url: p.sourceMetadata.url,
      snippet: p.sourceMetadata.extra?.snippet
    }
  }

  // Priorité 2: sourceMetadata.extra.messageLink (Slack)
  if (p.sourceMetadata?.extra?.messageLink) {
    return {
      url: p.sourceMetadata.extra.messageLink,
      snippet: p.sourceMetadata.extra?.text
    }
  }

  // Priorité 3: Chercher dans les justifications
  if (!proposal.justification || proposal.justification.length === 0) {
    return null
  }

  for (const justif of proposal.justification as any[]) {
    // Format dateDetails avec sources multiples
    if (justif.metadata?.dateDetails?.sources?.length > 0) {
      const firstSource = justif.metadata.dateDetails.sources[0]
      return {
        url: firstSource.source,
        snippet: firstSource.snippet,
        date: justif.metadata.dateDetails.date,
        confidence: justif.metadata.dateDetails.confidence
      }
    }

    // Format dateDetails simple
    if (justif.metadata?.dateDetails?.source) {
      return {
        url: justif.metadata.dateDetails.source,
        snippet: justif.metadata.dateDetails.snippet,
        date: justif.metadata.dateDetails.date,
        confidence: justif.metadata.dateDetails.confidence
      }
    }

    // type 'url_source'
    if (justif.type === 'url_source' && justif.metadata?.url) {
      return {
        url: justif.metadata.url,
        snippet: justif.metadata.snippet
      }
    }

    // metadata.source ou metadata.url
    if (justif.metadata?.source) {
      return {
        url: justif.metadata.source,
        snippet: justif.metadata.snippet
      }
    }
    if (justif.metadata?.url) {
      return {
        url: justif.metadata.url,
        snippet: justif.metadata.snippet
      }
    }
  }

  return null
}

// Labels lisibles pour les champs
const FIELD_LABELS: Record<string, string> = {
  // Event
  name: 'Nom',
  city: 'Ville',
  department: 'Département',
  country: 'Pays',
  description: 'Description',
  // Edition
  startDate: 'Date de début',
  endDate: 'Date de fin',
  year: 'Année',
  website: 'Site web',
  registrationUrl: 'Inscription',
  calendarStatus: 'Statut calendrier',
  timeZone: 'Fuseau horaire',
  // Race
  runDistance: 'Distance course (km)',
  bikeDistance: 'Distance vélo (km)',
  swimDistance: 'Distance natation (m)',
  walkDistance: 'Distance marche (km)',
  runPositiveElevation: 'D+ (m)',
  bikePositiveElevation: 'D+ vélo (m)',
  walkPositiveElevation: 'D+ marche (m)',
  price: 'Prix',
  categoryLevel1: 'Catégorie 1',
  categoryLevel2: 'Catégorie 2'
}

// Ordre des champs pour les courses (même ordre que RacesChangesTable)
const RACE_FIELDS_ORDER = [
  'name',
  'startDate',
  'categoryLevel1',
  'categoryLevel2',
  'runDistance',
  'bikeDistance',
  'swimDistance',
  'walkDistance',
  'runPositiveElevation',
  'bikePositiveElevation',
  'walkPositiveElevation',
  'price'
]

/**
 * Trie les différences de champs selon l'ordre défini dans fieldCategories.ts
 * Les champs non définis sont placés à la fin dans leur ordre original
 */
function sortFieldDiffs(diffs: FieldDiff[], entityType: 'EVENT' | 'EDITION' | 'RACE'): FieldDiff[] {
  const categories = getCategoriesForEntityType(entityType)
  // Créer un tableau plat de tous les champs dans l'ordre
  const fieldsOrder = categories.flatMap(cat => cat.fields)
  
  return [...diffs].sort((a, b) => {
    const indexA = fieldsOrder.indexOf(a.field)
    const indexB = fieldsOrder.indexOf(b.field)
    // Si un champ n'est pas dans l'ordre, le mettre à la fin
    const orderA = indexA === -1 ? 999 : indexA
    const orderB = indexB === -1 ? 999 : indexB
    return orderA - orderB
  })
}

interface SourceProposalPaneProps {
  /** Liste des propositions sources triées par priorité */
  sourceProposals: Proposal[]
  /** Index de la source active */
  activeSourceIndex: number
  /** Callback pour changer de source */
  onChangeSource: (index: number) => void
  /** Callback d'archivage d'une source (transmis aux onglets) */
  onArchiveSource?: (proposalId: string) => void
  /** Désactive les icônes archiver pendant une opération en cours */
  isArchiving?: boolean
  /** Différences de champs entre working et source */
  fieldDifferences: FieldDiff[]
  /** Différences de courses entre working et source */
  raceDifferences: RaceDiff[]
  /** Callback pour copier un champ */
  onCopyField: (field: string) => void
  /** Callback pour copier une course */
  onCopyRace: (sourceRaceId: string, targetRaceId?: string) => void
  /** Callback pour copier toute la proposition (fusion) */
  onCopyAll: () => void
  /**
   * Callback pour remplacer la working proposal par cette source (reset).
   * Optionnel pour rétro-compatibilité ; quand fourni, ajoute un second
   * bouton sous "Fusionner avec cette proposition".
   */
  onReplaceAll?: () => void
  /**
   * État courant des modifications utilisateur, utilisé pour afficher un
   * compteur précis dans le Dialog de confirmation du replace (« 3 champs
   * édités, 2 suppressions manuelles seront annulées »).
   * Optionnels — si absents, le Dialog affiche le warning générique.
   */
  userModifiedChanges?: Record<string, any>
  userModifiedRaceChanges?: Record<string, any>
  /** Les blocs sont-ils validés (désactive les copies) */
  isValidated?: boolean
}

/**
 * Formatte une valeur pour l'affichage
 */
function formatValue(field: string, value: any): string {
  if (value === null || value === undefined) return '-'

  // Dates
  if (field === 'startDate' || field === 'endDate') {
    try {
      return formatDateInTimezone(String(value), 'Europe/Paris')
    } catch {
      // Fallback
    }
    return String(value)
  }

  // Distances avec unité
  if (field === 'runDistance' || field === 'bikeDistance' || field === 'walkDistance') {
    return `${value} km`
  }
  if (field === 'swimDistance' || field === 'runPositiveElevation' ||
      field === 'bikePositiveElevation' || field === 'walkPositiveElevation') {
    return `${value} m`
  }
  if (field === 'price') {
    return `${value} €`
  }

  return String(value)
}

/**
 * SourceProposalPane - Pane droit affichant les données sources en lecture seule
 * 
 * Affiche :
 * - Onglets pour sélectionner la source
 * - Bouton pour copier toute la proposition
 * - Tables des champs Event, Edition et Courses avec boutons [←] de copie
 * - Indicateurs visuels des différences (⚠️ différent, ✓ identique, 💭 absent)
 */
const SourceProposalPane: React.FC<SourceProposalPaneProps> = ({
  sourceProposals,
  activeSourceIndex,
  onChangeSource,
  onArchiveSource,
  isArchiving,
  fieldDifferences,
  raceDifferences,
  onCopyField,
  onCopyRace,
  onCopyAll,
  onReplaceAll,
  userModifiedChanges,
  userModifiedRaceChanges,
  isValidated = false
}) => {
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false)

  // Compteur de modifications utilisateur en attente, pour le Dialog de
  // confirmation du replace. On distingue : champs Event/Edition édités,
  // courses ajoutées manuellement (new-X), suppressions/keep manuels, et
  // simples edits de courses existantes.
  const modificationStats = React.useMemo(() => {
    const stats = {
      fields: 0,
      racesAdded: 0,
      racesDeleted: 0,
      racesKeptDespiteAgent: 0,
      racesEdited: 0,
    }
    if (userModifiedChanges) {
      stats.fields = Object.keys(userModifiedChanges).filter(k => k !== 'raceEdits').length
    }
    if (userModifiedRaceChanges) {
      for (const [raceId, mods] of Object.entries(userModifiedRaceChanges)) {
        const m = mods as Record<string, any>
        const hasDeleted = m._deleted === true
        const hasKeep = m._keepProposedDeletion === true
        const otherFields = Object.keys(m).filter(
          k => k !== '_deleted' && k !== '_keepProposedDeletion' && k !== '_isNew' && k !== '_copiedFromSource'
        )
        if (raceId.startsWith('new-') && !hasDeleted) {
          stats.racesAdded++
        } else if (hasDeleted) {
          stats.racesDeleted++
        } else if (hasKeep) {
          stats.racesKeptDespiteAgent++
        }
        if (otherFields.length > 0 && !hasDeleted) {
          stats.racesEdited++
        }
      }
    }
    return stats
  }, [userModifiedChanges, userModifiedRaceChanges])

  const totalModifications =
    modificationStats.fields +
    modificationStats.racesAdded +
    modificationStats.racesDeleted +
    modificationStats.racesKeptDespiteAgent +
    modificationStats.racesEdited
  if (sourceProposals.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Aucune source disponible
        </Typography>
      </Paper>
    )
  }

  const activeSource = sourceProposals[activeSourceIndex]
  const isSourceReadOnly = isSourceProposalReadOnly(activeSource?.status)
  const copyDisabled = isValidated || isSourceReadOnly

  const readOnlyLabel =
    activeSource?.status === 'APPROVED'
      ? 'validée'
      : activeSource?.status === 'REJECTED'
        ? 'rejetée'
        : activeSource?.status === 'ARCHIVED'
          ? 'archivée'
          : ''

  // Séparer les différences par bloc et trier selon l'ordre défini dans fieldCategories.ts
  const eventCategories = getCategoriesForEntityType('EVENT')
  const eventFields = eventCategories.flatMap(cat => cat.fields)
  const eventDiffsUnsorted = fieldDifferences.filter(d => eventFields.includes(d.field))
  const eventDiffs = sortFieldDiffs(eventDiffsUnsorted, 'EVENT')

  const editionCategories = getCategoriesForEntityType('EDITION')
  const editionFields = editionCategories.flatMap(cat => cat.fields)
  const editionDiffsUnsorted = fieldDifferences.filter(d => editionFields.includes(d.field))
  const editionDiffs = sortFieldDiffs(editionDiffsUnsorted, 'EDITION')

  // Compter les courses dans la source
  const sourcesRacesCount = raceDifferences.filter(r => r.existsInSource).length

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Onglets de sélection de source */}
      <SourceTabs
        sourceProposals={sourceProposals}
        activeIndex={activeSourceIndex}
        onChangeSource={onChangeSource}
        onArchiveSource={onArchiveSource}
        isArchiving={isArchiving}
      />

      {/* Bandeau d'info pour les sources non éditables */}
      {isSourceReadOnly && (
        <Alert
          severity="info"
          icon={<LockIcon fontSize="inherit" />}
          sx={{ borderRadius: 0 }}
        >
          Cette source a déjà été {readOnlyLabel} — lecture seule, copie désactivée.
        </Alert>
      )}

      {/* Contenu — opacité réduite quand la source est read-only */}
      <Box sx={{ opacity: isSourceReadOnly ? 0.6 : 1 }}>
      {/* Boutons d'application de la source à la working proposal */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onCopyAll}
            disabled={copyDisabled}
            fullWidth
          >
            Fusionner avec cette proposition
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Reporte les champs et courses de cette source qui diffèrent du
            working sans rien retirer.
          </Typography>
        </Box>

        {onReplaceAll && (
          <Box>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SwapHorizIcon />}
              onClick={() => setIsReplaceConfirmOpen(true)}
              disabled={copyDisabled}
              fullWidth
            >
              Remplacer par cette proposition
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Reset complet : cette source devient la proposition prioritaire.
              Les modifications utilisateur, les blocs validés et les champs /
              courses absents de cette source disparaissent.
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog
        open={isReplaceConfirmOpen}
        onClose={() => setIsReplaceConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remplacer la proposition de travail ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action est destructive. La working proposal sera entièrement
            remplacée par la source <strong>{activeSource?.agentId || 'sélectionnée'}</strong> :
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
            <li>
              <Typography variant="body2" component="div">
                {totalModifications === 0 ? (
                  <>
                    Vos modifications manuelles seraient perdues — pour
                    l'instant, vous n'avez fait <strong>aucune édition</strong>{' '}
                    sur cette working proposal.
                  </>
                ) : (
                  <>
                    <strong>
                      Vos modifications manuelles seront perdues
                    </strong>{' '}
                    :
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                      {modificationStats.fields > 0 && (
                        <li>
                          {modificationStats.fields} champ
                          {modificationStats.fields > 1 ? 's édités' : ' édité'}
                        </li>
                      )}
                      {modificationStats.racesEdited > 0 && (
                        <li>
                          {modificationStats.racesEdited} course
                          {modificationStats.racesEdited > 1 ? 's éditées' : ' éditée'}
                        </li>
                      )}
                      {modificationStats.racesAdded > 0 && (
                        <li>
                          {modificationStats.racesAdded} course
                          {modificationStats.racesAdded > 1
                            ? 's ajoutées manuellement'
                            : ' ajoutée manuellement'}
                        </li>
                      )}
                      {modificationStats.racesDeleted > 0 && (
                        <li>
                          {modificationStats.racesDeleted} suppression
                          {modificationStats.racesDeleted > 1 ? 's manuelles' : ' manuelle'}{' '}
                          de course
                        </li>
                      )}
                      {modificationStats.racesKeptDespiteAgent > 0 && (
                        <li>
                          {modificationStats.racesKeptDespiteAgent} annulation
                          {modificationStats.racesKeptDespiteAgent > 1 ? 's' : ''}{' '}
                          de suppression proposée par un agent
                        </li>
                      )}
                    </Box>
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Les champs et courses présents dans la working mais absents de
                la source <strong>disparaîtront</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Les blocs déjà validés sont <strong>dévalidés</strong> — vous
                devrez les re-valider après revue.
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsReplaceConfirmOpen(false)}>
            Annuler
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setIsReplaceConfirmOpen(false)
              onReplaceAll?.()
            }}
          >
            Remplacer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section Événement */}
      {eventDiffs.length > 0 && (
        <Box>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover'
            }}
          >
            <EventIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Événement</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Champ</TableCell>
                  <TableCell sx={{ width: '55%' }}>Valeur</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Copier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventDiffs.map(diff => (
                  <FieldDiffRow
                    key={diff.field}
                    diff={diff}
                    onCopy={onCopyField}
                    disabled={copyDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Section Édition */}
      {editionDiffs.length > 0 && (
        <Box>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <CalendarIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Édition</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Champ</TableCell>
                  <TableCell sx={{ width: '55%' }}>Valeur</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Copier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editionDiffs.map(diff => (
                  <FieldDiffRow
                    key={diff.field}
                    diff={diff}
                    onCopy={onCopyField}
                    disabled={copyDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Section Courses */}
      {raceDifferences.length > 0 && (
        <Box>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'action.hover',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RaceIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2">
                Courses ({sourcesRacesCount})
              </Typography>
            </Box>
            <CopyAllRacesButton
              onCopyAll={onCopyAll}
              disabled={copyDisabled}
              racesCount={sourcesRacesCount}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Champ</TableCell>
                  <TableCell sx={{ width: '55%' }}>Valeur</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Copier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {raceDifferences
                  .filter(race => race.existsInSource)
                  .map(race => (
                    <RaceDiffRows
                      key={race.raceId}
                      race={race}
                      onCopyRace={onCopyRace}
                      disabled={copyDisabled}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Section Source - URL et snippet de la proposition active */}
      <SourceInfoSection proposal={activeSource} />
      </Box>
    </Paper>
  )
}

/**
 * Ligne affichant un champ avec son statut de différence
 */
interface FieldDiffRowProps {
  diff: FieldDiff
  onCopy: (field: string) => void
  disabled: boolean
}

const FieldDiffRow: React.FC<FieldDiffRowProps> = ({ diff, onCopy, disabled }) => {
  const label = FIELD_LABELS[diff.field] || diff.field
  const isDifferent = diff.isDifferent && !diff.isAbsentInSource

  return (
    <TableRow
      sx={(theme) => ({
        bgcolor: isDifferent
          ? (theme.palette.mode === 'dark' 
              ? 'rgba(237, 108, 2, 0.08)' 
              : 'rgba(237, 108, 2, 0.04)')
          : 'inherit'
      })}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {diff.isAbsentInSource ? (
            <AbsentIcon fontSize="small" color="disabled" />
          ) : isDifferent ? (
            <WarningIcon fontSize="small" color="warning" />
          ) : (
            <CheckIcon fontSize="small" color="success" />
          )}
          <Typography
            variant="body2"
            fontWeight={isDifferent ? 'bold' : 500}
          >
            {label}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={diff.isAbsentInSource ? 'text.disabled' : (isDifferent ? 'primary.main' : 'text.primary')}
          sx={{ fontStyle: diff.isAbsentInSource ? 'italic' : 'normal' }}
        >
          {diff.isAbsentInSource ? 'Absent dans cette source' : formatValue(diff.field, diff.sourceValue)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <CopyFieldButton
          fieldName={diff.field}
          fieldLabel={label}
          onCopy={onCopy}
          isDifferent={isDifferent}
          disabled={disabled}
        />
      </TableCell>
    </TableRow>
  )
}

/**
 * Lignes affichant une course avec le même format que l'Édition
 * - Ligne séparateur avec nom de la course + badge différences + bouton copier tout
 * - Lignes de champs (Champ / Valeur / Copier) pour chaque différence
 */
interface RaceDiffRowsProps {
  race: RaceDiff
  onCopyRace: (sourceRaceId: string, targetRaceId?: string) => void
  disabled: boolean
}

const RaceDiffRows: React.FC<RaceDiffRowsProps> = ({ race, onCopyRace, disabled }) => {
  const hasDifferences = race.fieldDiffs.some(d => d.isDifferent)
  const isNew = !race.existsInWorking
  const differentFields = race.fieldDiffs.filter(d => d.isDifferent)
  
  // Pour une nouvelle course, afficher tous les champs avec valeur
  const fieldsToShowUnsorted = isNew 
    ? race.fieldDiffs.filter(d => d.sourceValue !== undefined && d.sourceValue !== null)
    : differentFields
  
  // Trier les champs selon l'ordre défini (même ordre que RacesChangesTable)
  const fieldsToShow = [...fieldsToShowUnsorted].sort((a, b) => {
    const indexA = RACE_FIELDS_ORDER.indexOf(a.field)
    const indexB = RACE_FIELDS_ORDER.indexOf(b.field)
    // Si un champ n'est pas dans l'ordre, le mettre à la fin
    const orderA = indexA === -1 ? 999 : indexA
    const orderB = indexB === -1 ? 999 : indexB
    return orderA - orderB
  })

  let statusLabel: string
  let statusColor: 'success' | 'warning' | 'info' = 'info'

  if (isNew) {
    statusLabel = 'Nouvelle'
    statusColor = 'success'
  } else if (hasDifferences) {
    statusLabel = `${differentFields.length} diff.`
    statusColor = 'warning'
  } else {
    statusLabel = 'Identique'
    statusColor = 'info'
  }

  return (
    <>
      {/* Ligne séparateur avec nom de la course */}
      <TableRow
        sx={(theme) => ({
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.04)'
        })}
      >
        <TableCell colSpan={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {race.raceName}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              color={statusColor}
              variant="outlined"
              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
            />
          </Box>
        </TableCell>
        <TableCell align="right">
          {(isNew || hasDifferences) && (
            <CopyRaceButton
              sourceRaceId={race.sourceRaceId || race.raceId}
              raceName={race.raceName}
              existsInWorking={race.existsInWorking}
              targetRaceId={race.workingRaceId}
              onCopy={onCopyRace}
              disabled={disabled}
            />
          )}
        </TableCell>
      </TableRow>

      {/* Lignes de champs - même format que l'Édition */}
      {fieldsToShow.map(diff => {
        const label = FIELD_LABELS[diff.field] || diff.field
        const isDifferent = diff.isDifferent && !diff.isAbsentInSource

        return (
          <TableRow
            key={diff.field}
            sx={(theme) => ({
              bgcolor: isDifferent
                ? (theme.palette.mode === 'dark' 
                    ? 'rgba(237, 108, 2, 0.08)' 
                    : 'rgba(237, 108, 2, 0.04)')
                : 'inherit'
            })}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isDifferent ? (
                  <WarningIcon fontSize="small" color="warning" />
                ) : (
                  <CheckIcon fontSize="small" color={isNew ? 'success' : 'disabled'} />
                )}
                <Typography
                  variant="body2"
                  fontWeight={isDifferent ? 'bold' : 500}
                >
                  {label}
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Typography
                variant="body2"
                color={isDifferent ? 'primary.main' : 'text.primary'}
              >
                {formatValue(diff.field, diff.sourceValue)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              {/* Pas de bouton copier individuel pour les champs de course */}
            </TableCell>
          </TableRow>
        )
      })}

      {/* Message si aucune différence */}
      {!isNew && !hasDifferences && (
        <TableRow>
          <TableCell colSpan={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
              Aucune différence
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

/**
 * Section affichant les informations de la proposition source :
 * - URL source et snippet
 * - Agent, date de création, lien vers la proposition
 */
interface SourceInfoSectionProps {
  proposal: Proposal
}

const SourceInfoSection: React.FC<SourceInfoSectionProps> = ({ proposal }) => {
  const sourceInfo = extractSourceInfo(proposal)
  const p = proposal as any

  const formatDate = (dateString: string) => {
    return formatDateInTimezone(dateString, 'Europe/Paris', 'dd MMM yyyy à HH:mm')
  }

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider'
      }}
    >
      {/* Section Source URL + Snippet */}
      {sourceInfo && (
        <>
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover'
            }}
          >
            <LinkIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Source</Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {/* URL */}
            <Typography
              component="a"
              href={sourceInfo.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
                display: 'block',
                mb: sourceInfo.snippet ? 2 : 0,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {sourceInfo.url}
            </Typography>

            {/* Snippet */}
            {sourceInfo.snippet && (
              <Box
                sx={(theme) => ({
                  p: 1.5,
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderLeft: 3,
                  borderColor: 'info.main',
                  borderRadius: 1
                })}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', lineHeight: 1.5 }}
                >
                  "{sourceInfo.snippet}"
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Section Métadonnées de la proposition */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'action.hover',
          borderTop: sourceInfo ? 1 : 0,
          borderColor: 'divider'
        }}
      >
        <AgentIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2">Proposition</Typography>
      </Box>

      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Agent */}
        {p.agent?.name && (
          <Chip
            label={p.agent.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 22 }}
          />
        )}

        {/* Date de création */}
        {p.createdAt && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {formatDate(p.createdAt)}
            </Typography>
          </Box>
        )}

        {/* Lien vers la proposition */}
        <Button
          component={Link}
          to={`/proposals/${proposal.id}`}
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
          sx={{ ml: 'auto', textTransform: 'none' }}
        >
          Voir détails
        </Button>
      </Box>
    </Box>
  )
}

export default SourceProposalPane
